// ============================================================================
// Theater Builder — 客厅 3D 场景（房间用模型 · 固定正视角）
// ============================================================================
//
// 这个文件用 Three.js 在一个 <canvas> 里渲染一个 3D 影音室：
//   - 加载房间模型 GLB（影音室外壳，自带氛围；02 照明 = 在 4 间不同房间 GLB 之间切换，见 ROOMS）
//   - 每间房一套「固定柔光」：半球光 + 主方向光 + 补光 + RoomEnvironment 反射（不逐级调暗）
//   - 往房间里只叠 4 样：幕布 + 沙发 + 地柜 + 投影仪（都是单独的产品 GLB）
//   - 【固定视角】初始即正面面对投影仪/幕布墙，不允许用户拖动 / 缩放
//
// 自托管 three（不再走第三方 esm.sh）：three 核心 + GLTFLoader + RoomEnvironment 已用 esbuild
// 打成一个自包含包 assets/three-bundle.js —— 裸标识符全部内联，不需要 importmap（Impact 在 <head>
// 就关了 importmap 窗口），也只有一个 THREE 实例。相对路径引入：本文件与 three-bundle.js 同在
// assets/，Shopify CDN 上同目录。⚠ 重打包/升级 three 版本的命令见 docs/three-bundle.md。

import * as THREE from './three-bundle.js';
import { GLTFLoader, RoomEnvironment } from './three-bundle.js';

// ---------------------------------------------------------------------------
// 资源 URL —— 只是「叠加在房间里」的产品模型（房间 GLB 见下方 ROOMS）
// ---------------------------------------------------------------------------
//   /3d/models/{hash}/*.glb 是 Shopify 产品3D媒体。02 照明 = 切不同房间 GLB(每间自带氛围),
//   代码只往房间里叠 4 样：屏幕(3 形态) / 投影仪(2×2) / 沙发 / 地柜。
const ASSETS = {
  screenFixed: 'https://cdn.shopify.com/3d/models/e163b481bab3cc6f/_-opt.glb',                          // 屏幕·画框固定式（已压缩）
  screenDrop:  'https://cdn.shopify.com/3d/models/65fb23b0bbfbb3e8/_-opt.glb',                          // 屏幕·降落屏（已压缩）
  screenFloor: 'https://cdn.shopify.com/3d/models/cdcf95a4a0cee42e/_-opt.glb',                          // 屏幕·地升屏（已压缩）
  projector:        'https://cdn.shopify.com/3d/models/9b81abb50c17c5ca/_.glb',                       // 短焦·桌面正装
  projectorCeiling: 'https://cdn.shopify.com/3d/models/b11eb6b201f6e8c8/_.glb',                       // 短焦·吸顶倒装
  projectorLong:        'https://cdn.shopify.com/3d/models/bb2d552030d95711/_-_3.glb',                 // 长焦·桌面正装
  projectorCeilingLong: 'https://cdn.shopify.com/3d/models/3defd0cc65ba5832/_1-opt.glb',                 // 长焦·吸顶倒装
  sofa:      'https://cdn.shopify.com/3d/models/31ec75ef8260c45c/_1.glb',                             // 沙发
  cabinet:   'https://cdn.shopify.com/3d/models/02c2a0a88a0bd3b6/_1.glb'                              // 地柜
};

// ---------------------------------------------------------------------------
// 入口函数 —— 由 sections/theater-builder.liquid 调用
// ---------------------------------------------------------------------------
//   opts.containerId —— 外层 <div> 的 id
//   opts.roomUrl     —— （可选）房间 GLB 的 URL，覆盖当前房间 url（本地预览用）
//   opts.roomKey     —— （可选）起始房间 key（'dark'/'night'/...），本地预览单独测某间房用
export async function initTheaterBuilder(opts) {
  const container = document.getElementById(opts.containerId);
  if (!container) return;
  const canvas = container.querySelector('canvas');

  // ── 02 照明 = 切不同房间 GLB ──────────────────────────────────────────────
  //   每间房 GLB 自带氛围(墙/地/顶/灯/星空顶/壁挂音箱)，代码只往里叠:屏幕 / 投影仪 / (沙发) / (地柜)。
  //   每间房各存自己的一套配置(“每个房间位置可能都不一样”→ 位置都按房间各配,不配的项走通用默认):
  //     url       房间 GLB
  //     rotY      装载后绕 Y 旋转,把该间的“屏幕墙”统一转到 -X(R.minX);三间现都是 π,±π/2 也支持(量房自动换轴)
  //     mode      固定柔光 { light:{hemi,sun,fill,exposure,bg}, screenEmi }
  //     dims      量房覆盖(可选):自动量房失效的房间(开放式/带大地形板)→ 显式给“室内地板”在【原始模型坐标(未旋转)】的范围+层高
  //     screenInset/screenInsetLift  屏幕离 -X 墙的前移量(固定屏 / 升降屏);screenInsetDrop(可选)降落屏单独前移量——降落屏体薄(厚≈宽×0.04),与地升屏(底座厚 0.21)共用一个值会浮在墙前,单配才能贴墙;不配=同 screenInsetLift
  //     cabWallGap   地柜背板离 -X 墙的空隙(米):地柜整体前移、留出这段空隙给“地升屏”升起;从初始化固定、切屏不动
  //     hasSofa   false=房间自带沙发,不叠我们的沙发(默认 true=叠)
  //     longProj  长焦·桌面投影仪坐标【原始模型坐标】(可选):放“自带茶几”等指定台面;不配则默认摆我们沙发旁托盘
  //     selfLit   自发光照片背板【网格名】列表(可选):窗外远景照片板照不到光会全黑 → 点亮成不吃光的自发光(emissive 白 + 关色调映射)
  //     selfLitMat 自发光照片背板【材质名】列表(可选):网格名被多件共用的房间(如新 day 海景板与地板同名)→ 改按材质名匹配点亮
  //     wallCRaw  屏幕特征墙中心的【原始模型坐标】水平点 {x,z}(可选):特征墙不在房间正中的房间(night 石墙/bright 电视背板)→ 屏幕/地柜/投影仪/相机整组对齐到墙中心(取其世界 Z),否则相对墙左右露边不对称
  //     dropCeilY 降落屏吊装面离地高度(米,可选):屏幕墙上方有局部下吊顶/星空板的房间,降落屏外壳要吊在那块板的底面而非名义层高 R.H,否则外壳埋进吊顶
  const INITIAL_ROOM = (opts && opts.roomKey && ['dark','night','day','bright'].indexOf(opts.roomKey) > -1) ? opts.roomKey : 'dark';
  const ROOMS = {
    dark: {
      url: 'https://cdn.shopify.com/3d/models/4fe25ee4f2407b6c/dark-opt.glb',   // 压缩版 1.97MB(quantize+webp)。(默认首载房现为 bright → preload 那行盯 bright,不再盯这条)
      rotY: Math.PI,           // 幕布墙转到 -X
      screenInset: 0.65,       // 固定屏离 -X 墙的前移量(米):幕布墙是厚吸音结构,贴墙(0.05)会把屏幕吞掉 → 前移到墙面前。
      screenInsetLift: 0.33,   // 地升屏前移量:0.33 ≈ 底座刚好贴在幕布墙(navy 板)前;再小就被墙吞了。
      screenInsetDrop: 0.20,   // 降落屏单配:navy 板面实测在 R.minX+0.123、整高实墙(中部 Line087 挡射线曾误判墙矮)→ 共用 0.33 外壳浮在板前 16cm;0.20 默认宽背面离板 3cm、最大宽(3.9m)余 1.5cm
      cabWallGap: 0.50,        // 地柜背板离 -X 墙的空隙(米):地柜整体前移到此,地升屏(实测升在墙前 0.23~0.44m)就落在地柜后面这段空隙里;从初始化固定,切屏不再移动地柜。
      dropCeilY: 3.15,         // 降落屏吊装面:星空顶板(Box567)底面 3.153,比层高 3.19 低 → 外壳吊在星空板下,不戳进星空板。
      // ── dark 调光表:只动下面 mode 里这几个数就能微调这间房 ──
      //   exposure 总曝光 0.95 / hemi 半球光 0.55 / sun 主光 0.85 / fill 补光 0.30 / screenEmi 画面亮度 1.10 / bg 背景近黑
      //   ⚠ 只有 exposure/hemi/sun/fill 真照亮家具;屏幕/LED/星点是自发光面、只亮自己 → 屏幕亮度靠 screenEmi 调,LED/星点烤在 GLB 里改不了。
      mode: { light: { hemi: 0.55, sun: 0.85, fill: 0.30, exposure: 0.95, bg: 0x05070d }, screenEmi: 1.0 }
    },
    night: {
      url: 'https://cdn.shopify.com/3d/models/cdb626f5969d5b16/night-opt.glb',   // 去沙发+去圆桌版 3.92MB(quantize+webp)。⚠ 教训:换版模型几何可能平移(曾 +0.29X/+0.11Z),dims 必须重测——本版实测零平移;本地验证防同名旧文件被浏览器启发式缓存
      rotY: Math.PI,           // 同 dark:石材特征墙(原始 +X)转到 -X 当屏幕墙
      // 自动量房对 night 失效(开放式大客厅 + 外面大地形/弧形夜景板把包围盒撑成 18×14×17)→ 显式给“室内地板”范围。
      //   实测(原始模型坐标,去沙发版与 9febfc1aba82fd22 版逐字一致):室内地板 Rectangle017 X[-5.59,3.96]×Z[-3.7,4.6]、y=0;层高 3.15(石墙/天花)。
      dims: { rawMinX: -5.59, rawMaxX: 3.96, rawMinZ: -3.7, rawMaxZ: 4.6, rawFloorY: 0, H: 3.15 },
      // (2026-06-12 去沙发版:白沙发椅 + 圆桌都已在模型里删除 → 去掉 hasSofa:false 改上我们的沙发;
      //  去掉 longProj(圆桌没了),长焦·桌面机回默认沙发旁托盘 —— 与 dark/day/bright 完全同构)
      selfLit: ['Arc001'],     // 室外弧形夜景照片板:GLB 里夜景图在 emissiveMap 上但 emissive 因子=黑(和屏幕画面同病)→ 点亮成自发光照片背板,否则室外远处照不到光、从屋里看全黑
      selfLitEmi: 0.35,        // 夜景外景板压暗到 35%(0.6 仍偏亮:板 toneMapped=false 不吃房间曝光,夜景照片本身就亮)→ 单独调这个数(0.25 更暗 / 0.45 亮一点;默认 1.0)
      wallCRaw: { x: 0, z: 0.35 },   // 石墙(Rectangle016)原始 Z[-1.65,2.35] 中心=0.35,比量房中心(0.45)偏 0.1 → 屏幕/地柜/相机整组对齐石墙中心,否则左边露墙比右边多(rotY=π 时只用 z)
      dropCeilY: 3.0,          // 降落屏吊装面:石墙上方有下吊顶(Obj_Rectangle016,底面 3.0、比层高 3.15 低)→ 外壳吊在吊顶底面,否则埋进吊顶 0.15m
      screenInset: 0.60,       // night 屏幕墙是厚 0.8m 的文化石贴面(前沿 ≈ R.minX+0.53)→ 屏幕前移到石面前才不被吞;0.60 落在石面前
      screenInsetLift: 0.60,   // 降落屏实测背面离石面 2.9cm 正好贴墙 → 不需要 screenInsetDrop
      cabWallGap: 0.80,        // 地柜背板离石墙的空隙(米):石面在墙前 0.53m、地升屏升在墙前约 0.50~0.73m → 地柜背板退到 0.80m,地升屏落在地柜后面。
      // night 调光表:夜晚客厅(比 dark 影院亮一档,但仍是夜) exposure 1.0 / hemi 0.62 / sun 0.95 / fill 0.34 / 画面 1.0 / bg 暗蓝灰(露台开口透出夜色)
      mode: { light: { hemi: 0.62, sun: 0.95, fill: 0.34, exposure: 1.0, bg: 0x2a2f3a }, screenEmi: 1.0 }
    },
    day: {
      url: 'https://cdn.shopify.com/3d/models/4f27c126b47f6e9b/day-opt.glb',   // 第4版(重做)4.39MB(深石材特征墙/热带海滩外景,14.4万三角,quantize+webp)。⚠ 全新模型,配置整套重测
      rotY: Math.PI,           // 屏幕墙=中央盒体那面深色石材特征墙(原始 X≈3.44 平面、面朝 -X 客厅)→ 同其他房间转 π 到 -X
      // 开放 loft(客厅+中央石材盒体,两侧玻璃墙通海滩外景),海景板把包围盒撑成 13.7×4.9×13.7 → 显式量房,只取"客厅观影区":
      //   实测(原始坐标,射线):石材墙前沿 x≈3.44(=rawMaxX=R.minX);客厅朝 -X 全开放无后墙→rawMinX 取 -4.0 给 ~7.4m 纵深;层高 2.8(天花)。
      dims: { rawMinX: -4.0, rawMaxX: 3.44, rawMinZ: -3.0, rawMaxZ: 2.7, rawFloorY: 0, H: 2.8 },
      wallCRaw: { x: 3.44, z: 0.36 },   // 石材墙前沿 x3.44;屏幕组对齐特征盒体中心 raw z0.36(=世界 wallZ -0.50)→ 屏幕居中在石材墙上(早先取 0.2 偏左、用户要求右移已修);两侧玻璃开口:-Z 海滩+盆栽 / +Z 护栏阳台
      screenInset: 0.10,       // 石材面有 ~0.2m 凹凸板缝(前沿3.44~凹3.67)→ 比平墙多前移点,贴前沿不被凹板吞
      screenInsetLift: 0.14,
      screenInsetDrop: 0.10,
      cabWallGap: 0.30,
      selfLitMat: ['08 - 1Default3133'],   // 海滩外景板【按材质名】点亮:两块板(-Z 大背景 + +X 侧)网格名与地板/天花共用 JianE_model_*,只能按材质匹配
      // day 调光表:白天日光客厅(亮度仅次于 bright) exposure 1.05 / hemi 1.15 / sun 1.15 / fill 0.45 / 反弹色暖白 / 画面 1.0 / bg 雾蓝天色
      mode: { light: { hemi: 1.15, sun: 1.15, fill: 0.45, exposure: 1.05, bg: 0xbfcfdd, hemiGround: 0xf3efe8 }, screenEmi: 1.0 }
    },
    bright: {
      url: 'https://cdn.shopify.com/3d/models/3db0e033d892f749/bright-opt.glb',   // 模型师更新版 2.23MB(同房间壳+楼梯/绿植/木格栅/栏杆,75,410三角,simplify+quantize+webp)。坐标系同前,dims/wallCRaw/selfLit 零改动通用。⚠ 现为默认首载房:换它要同步 liquid 的 preload 行 + initTheaterBuilder 的 roomKey
      rotY: Math.PI,           // 屏幕墙=深木纹盒体那面墙(原始 x≈3.43 平面、面朝 -X 客厅)→ 同其他房间转 π 到 -X
      // 同房间壳+细节(楼梯/绿植/木格栅/栏杆,75,410三角):floor y0 X[-5.92,9.29]、天花 3.1、屏幕墙(2022-1747265_1)前沿 x3.43 z[-1.64,2.35]、外景板仅 +X 一块。
      //   rawMinX -4.0 给 ~7.4m 纵深;层高 3.1。外景板:网格 Line2132552412 / 材质 08 - Default3133。
      dims: { rawMinX: -4.0, rawMaxX: 3.43, rawMinZ: -3.5, rawMaxZ: 4.2, rawFloorY: 0, H: 3.1 },
      wallCRaw: { x: 3.43, z: 0.36 },   // 木纹墙前沿 x3.43;墙体 raw Z[-1.64,2.35] 中心 0.355→取 0.36 居中(两侧开口:+X 外景从屏幕墙后透出、-Z 开放露天色)
      screenInset: 0.06,       // 平整木纹墙 → 贴墙小前移
      screenInsetLift: 0.12,
      screenInsetDrop: 0.08,
      cabWallGap: 0.28,
      selfLit: ['Line2132552412'],   // 室外平面外景板(材质 08 - Default3133,+X 侧;rotY 后到屏幕墙后,从两侧开口透出)。emissiveMap 有图但 emissive=黑 同病 → 点亮
      // bright 调光表:白天明亮客厅(四档最亮) exposure 1.1 / hemi 1.3 / sun 1.3 / fill 0.5 / 反弹色暖白 / 画面 1.0 / bg 雾蓝天色
      mode: { light: { hemi: 1.3, sun: 1.3, fill: 0.5, exposure: 1.1, bg: 0xbfcfdd, hemiGround: 0xf3efe8 }, screenEmi: 1.0 }
    }
  };
  let currentRoomKey = INITIAL_ROOM;            // 当前房间(运行时换房间改它;现按 INITIAL_ROOM 起始)
  const cfg = () => ROOMS[currentRoomKey];      // 当前房间的配置取值口
  const ROOM_URL = (opts && opts.roomUrl) || cfg().url;

  // -------------------------------------------------------------------------
  // 场景 + 相机 + 渲染器
  // -------------------------------------------------------------------------
  const scene = new THREE.Scene();
  // 背景色占位:第一帧前 applyRoomLight 就会按当前房间的 bg 覆盖它。
  scene.background = new THREE.Color(0xe9eef3);

  // 固定相机：先占位，房间加载完后按真实尺寸重设位置 + lookAt。
  const camera = new THREE.PerspectiveCamera(50, 1, 0.05, 100);
  camera.position.set(2.5, 1.45, 0);
  camera.lookAt(-3, 1.2, 0);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // ACES 电影级色调映射;曝光值由每间房 mode.light.exposure 接管(applyRoomLight)。
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.localClippingEnabled = true;   // 允许按材质裁剪（屏幕"卷帘"升降动画用）

  // PMREM 环境贴图：给材质提供柔和的室内反射，PBR / 玻璃材质才不会发暗。
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  // -------------------------------------------------------------------------
  // 三件套灯具（实时，不烤光）—— 强度/反弹色/曝光由每间房 mode 接管(applyRoomLight)
  // -------------------------------------------------------------------------
  //   半球光：天空色(上) + 地面反弹色(下)，均匀提亮消死黑;地面反弹色是天花唯一的光源
  //   主方向光(sun)：暖白，从右上前方打来，给出明确受光方向
  //   补光(fill)：冷一点，从左后方补，压住阴影别太死
  const hemi = new THREE.HemisphereLight(0xffffff, 0xccd1d6, 1.25);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xfff3e3, 2.4);
  sun.position.set(7, 11, 5);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0xdcebff, 0.7);
  fill.position.set(-6, 5, -4);
  scene.add(fill);

  // =========================================================================
  // 02 照明 —— 切房间 + 每间固定柔光
  // =========================================================================
  //   每间房 GLB 自带氛围,代码只给这间套一套「固定柔光」——4 个照明按钮 = 换房间,不在同一间里调明暗。
  //   light : 半球光/主光/补光强度 + 曝光 + 背景天色(bg)   screenEmi: 画面自发光强度(房间越暗越往上调)
  //   注意:只有 hemi/sun/fill/exposure 这套光真照亮家具;屏幕画面是自发光面、只能靠 screenEmi 调。
  let CUR = cfg().mode;   // 当前这间房的固定柔光配置

  // 屏幕"画面"自发光强度：带画面的屏材质纳入这个集合，按当前房间的 screenEmi 统一设亮度。
  const screenImageMats = new Set();
  function applyScreenEmissive() { screenImageMats.forEach((m) => { m.emissiveIntensity = CUR.screenEmi; }); }

  // 套用某间房的固定柔光（打光 + 曝光 + 背景 + 画面亮度）。换房间时换 mode 再调一次即可。
  function applyRoomLight(mode) {
    CUR = mode;
    const L = mode.light;
    hemi.intensity = L.hemi; sun.intensity = L.sun; fill.intensity = L.fill;
    hemi.groundColor.set(L.hemiGround != null ? L.hemiGround : 0xccd1d6);   // 地面反弹色:照亮"朝下的面"(天花)的唯一光源;白亮房间(bright)配暖白,默认蓝灰
    renderer.toneMappingExposure = L.exposure;
    scene.background.set(L.bg);
    applyScreenEmissive();
  }
  // 02 照明 4 按钮(0.05/0.3/0.6/0.9 → dark/night/day/bright)。liquid 调的仍是 setBrightness 这个名字(不用改 liquid)。
  const roomKeyFromT = (t) => (t < 0.18 ? 'dark' : t < 0.45 ? 'night' : t < 0.78 ? 'day' : 'bright');
  function setBrightness(t) {
    t = Math.max(0, Math.min(1, +t || 0));
    setRoom(roomKeyFromT(t));   // 02 照明 4 档 → 切到对应房间:同 GLB(day 现别名 dark)只切光;不同 GLB 走缓存/加载+重摆家具。setRoom 见下方。
  }
  // 首帧:liquid 要稍后才调 setBrightness(创建时是亮日光基准) → 立刻套一次这间的固定柔光,曝光/背景从第一帧就对。
  applyRoomLight(CUR);

  // -------------------------------------------------------------------------
  // 自适应大小 + 渲染循环（固定相机：无轨道控制、无剖切，每帧只渲染）
  // -------------------------------------------------------------------------
  // 竖屏取景修正：fov 是「竖直」视野，手机竖屏(窄)会把横向的客厅挤窄。
  // 当画面竖高(aspect<1)时，抬高竖直 fov，把「水平」视野锁回 aspect=1 时的值(≈50°)，
  // 房间重新填满画面；竖直 fov 封顶 80° 避免过度广角畸变。横屏/桌面照旧用 BASE_FOV。
  const BASE_FOV = 50;
  const D2R = Math.PI / 180;
  const REF_TAN = Math.tan((BASE_FOV / 2) * D2R);   // aspect=1 时的水平半角正切(=tan25°)
  let lastW = 0, lastH = 0;
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;   // 量 canvas 本身(不是容器):手机端容器是纵向 flex、下方还排着面板,只有 canvas 才是真正的场景显示区
    if (!w || !h) return;
    if (w === lastW && h === lastH) return;   // 尺寸未变就不重设:防手机滚动时地址栏伸缩/ResizeObserver 抖动→反复 setSize 重建缓冲→场景闪烁
    lastW = w; lastH = h;
    renderer.setSize(w, h, false);
    const aspect = w / h;
    camera.aspect = aspect;
    camera.fov = aspect < 1 ? Math.min(2 * Math.atan(REF_TAN / aspect) / D2R, 80) : BASE_FOV;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(canvas);   // 观察 canvas:手机端切竖排/转屏时 canvas 显示尺寸变化即重算(setSize 用 updateStyle=false,不改 CSS 尺寸→不会自触发循环)
  resize();

  // 相机平滑过渡：applyView() 只设"目标位置/朝向"，渲染循环每帧把相机 lerp 过去 →
  // 切换安装方式(桌面↔吸顶)、拖观看距离时画面平滑移动，不生硬跳。
  const camPosTarget  = new THREE.Vector3().copy(camera.position);
  const camLookTarget = new THREE.Vector3(-3, 1.2, 0);
  const camLookNow    = new THREE.Vector3(-3, 1.2, 0);

  // 屏幕"卷帘"升降动画：一张水平裁剪面把幕布从底座/外壳里逐渐揭开（地升=由下往上，降落=由上往下）。
  const screenClip = new THREE.Plane(new THREE.Vector3(0, -1, 0), 1e6);   // 1e6 = 不裁剪（全显）
  let screenAnim = null;
  let revealToken = 0;   // 每次播放卷帘自增；兜底定时器据此判断自己是否已被更晚的切换取代

  // 渲染循环每帧只做：相机 lerp + 渲染（只用早已声明的变量 → 加载期也能进度式渲染模型逐个 pop 进来）。
  // updateSizeLabel / 沙发 lerp 引用 screenForm / sizeLabel / sofaRef 等「晚声明」的 let/const，而 init 顶层在它们
  // 之前有 `await loadRoomModel`(房间 GLB，网络加载几百 ms)——加载期 rAF 已在跑，此时碰这些变量会撞 TDZ。
  // 故用 initReady 门控：等 init 同步声明全部跑完(下方 updateSizeLabel 定义后置 true)再执行这部分。
  let initReady = false;
  function loop() {
    requestAnimationFrame(loop);
    camera.position.lerp(camPosTarget, 0.12);
    camLookNow.lerp(camLookTarget, 0.12);
    camera.lookAt(camLookNow);
    if (initReady) {
      if (screenAnim) updateScreenAnim();
      updateSizeLabel();   // 把「推荐尺寸」标签贴到当前可见屏的画面正中(降落/地升卷帘动画结束后再现、换屏/换房间实时跟随)
      if (sofaRef) { sofaRef.position.lerp(sofaPosTarget, 0.12); placeLongTable(); }   // 沙发与相机同速平滑跟随(消除拖滑块步进卡顿)；长焦桌面机贴沙发托盘一起动
      lerpForms();        // 幕布缩放与沙发同速平滑推进(拖滑杆时尺寸不再一格格跳)
      applyRoomGhost();   // 01 按住距离滑杆:房间壳蓝图虚化/松手恢复的平滑过渡
    }
    renderer.render(scene, camera);
  }
  requestAnimationFrame(loop);

  // -------------------------------------------------------------------------
  // 加载器 + 工具
  // -------------------------------------------------------------------------
  const gltf = new GLTFLoader();
  // 网络抖动重试：弱网下大文件偶发 ERR_CONNECTION_CLOSED / QUIC 错误，
  // 单次 fetch 失败整个模型就没了 → 退避重试，最多 4 次（绝大多数第 2~3 次即成功）。
  const loadModel = async (url, label, tries = 4) => {
    for (let i = 1; i <= tries; i++) {
      try {
        return await gltf.loadAsync(url);
      } catch (e) {
        console.warn('[theater-builder] 模型加载失败 (第 ' + i + '/' + tries + ' 次): ' + label, e);
        if (i < tries) await new Promise((r) => setTimeout(r, 500 * i));   // 0.5s / 1s / 1.5s 退避
      }
    }
    return null;
  };
  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  // 贴图过滤修正:各向异性拉满 + 把 glTF 导出成「无 mipmap」(LinearFilter)的贴图改回三线性 mipmap。
  //   否则密集纹理(如地升屏吸音孔板的圆孔贴图)远看缩小会出摩尔纹/椭圆纹。对所有贴图槽生效。
  const fixTex = (m) => {
    if (!m) return;
    ['map', 'emissiveMap', 'aoMap', 'roughnessMap', 'metalnessMap', 'normalMap'].forEach((k) => {
      const tx = m[k];
      if (!tx) return;
      tx.anisotropy = maxAniso;
      if (tx.minFilter === THREE.LinearFilter) { tx.minFilter = THREE.LinearMipmapLinearFilter; tx.generateMipmaps = true; tx.needsUpdate = true; }
    });
  };

  // 房间真实范围（米）—— 房间加载完后按模型包围盒填入；幕布/家具/相机都按它来。
  let R = { W: 7, D: 6.24, H: 3, minX: -3.5, maxX: 3.5, minZ: -3.12, maxZ: 3.12 };
  let roomRef = null;        // 房间对象引用（运行时换房间时移除旧房间用）
  let longProjPos = null;    // 长焦·桌面投影仪的指定台面位（世界坐标）：某些房间(night)放“自带茶几”上,由 ROOMS[].longProj 算得
  let wallZ = 0;             // 屏幕特征墙中心的世界 Z:特征墙不在房间正中的房间(night 石墙/bright 电视背板)由 ROOMS[].wallCRaw 算得;屏幕/地柜/投影仪/相机整组锚到它(默认 0=房间正中)
  let roomToken = 0;         // 换房间令牌:防止快速连点导致旧的异步换房间覆盖新的
  let sofaStarted = false;   // 我们的沙发是否已开始加载(懒加载,自带沙发的房间不加载)

  // wrapModel —— 把产品 GLB 标准化（缩放到目标尺寸 + 重定锚点）
  //   targetMax：缩放后最长边的米数；anchor：'bottom-center' / 'top-center' / 'center'
  //   返回外层 Group：position = 锚点世界位置；userData.size = 缩放后实际尺寸
  function wrapModel(srcScene, targetMax, anchor) {
    srcScene.updateMatrixWorld(true);
    const bb1 = new THREE.Box3().setFromObject(srcScene);
    const sz = bb1.getSize(new THREE.Vector3());
    const maxDim = Math.max(sz.x, sz.y, sz.z, 1e-4);
    srcScene.scale.setScalar(targetMax / maxDim);

    srcScene.updateMatrixWorld(true);
    const bb2 = new THREE.Box3().setFromObject(srcScene);
    const c = bb2.getCenter(new THREE.Vector3());
    let ap;
    if (anchor === 'top-center')         ap = new THREE.Vector3(c.x, bb2.max.y, c.z);
    else if (anchor === 'bottom-center') ap = new THREE.Vector3(c.x, bb2.min.y, c.z);
    else                                 ap = c.clone();
    srcScene.position.sub(ap);

    srcScene.traverse((o) => {
      if (o.isMesh && o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach(fixTex);   // 各向异性 + minFilter mipmap 修正(防摩尔纹)
      }
    });

    const wrapper = new THREE.Group();
    wrapper.add(srcScene);
    wrapper.userData.size = bb2.getSize(new THREE.Vector3());
    return wrapper;
  }

  // 把屏幕模型按「幕面」水平居中（而不是按整体包围盒）。
  //   有些屏幕 GLB（如地升屏）带单边的非对称构件，导致包围盒中心 ≠ 幕面中心，
  //   wrapModel 按包围盒居中就会让画面偏到一边。这里找最大的平面网格(=幕布面)，
  //   把内层在水平面(X/Z)上平移，使幕面中心落到 wrapper 原点。竖向(Y)不动——交给锚定逻辑。
  //   必须在 wrapper 旋转/定位前调用（此时 wrapper 仍是单位变换，量到的就是 wrapper 局部坐标）。
  function recenterScreenPanel(wrapper) {
    wrapper.updateMatrixWorld(true);
    const tmp = new THREE.Box3(), sz = new THREE.Vector3();
    let best = null, bestArea = -1, bestBox = null;
    wrapper.traverse((o) => {
      if (!o.isMesh) return;
      tmp.setFromObject(o); if (tmp.isEmpty()) return;
      tmp.getSize(sz);
      const d = [sz.x, sz.y, sz.z].sort((a, b) => b - a);   // 两条最长边之积 = 平面面积
      const area = d[0] * d[1];
      if (area > bestArea) { bestArea = area; best = o; bestBox = tmp.clone(); }
    });
    if (!best) return;
    wrapper.userData.panelMesh = best;   // 画面网格(最大面积面)→ 尺寸标签据此精确定位到画面正中
    const fsz = bestBox.getSize(new THREE.Vector3());
    wrapper.userData.panelWidth = Math.max(fsz.x, fsz.y, fsz.z);   // 幕布最宽边（归一化空间）→ 三种屏按它统一缩放，画面同宽
    const c = bestBox.getCenter(new THREE.Vector3());
    const inner = wrapper.children[0];
    if (inner) { inner.position.x -= c.x; inner.position.z -= c.z; }   // 只在水平面对齐幕面
  }

  // -------------------------------------------------------------------------
  // 加载房间模型 —— 居中、落地、量尺寸、调材质;带已载缓存(02 来回切换零等待)。
  //   首屏载入起始房间;运行时“点 02 按钮换房间”也调它。
  // -------------------------------------------------------------------------
  function disposeTree(obj) {   // 缓存淘汰时释放该房间的几何/材质/贴图,避免显存累积
    obj.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        if (!m) return;
        ['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'].forEach((k) => { if (m[k] && m[k].dispose) m[k].dispose(); });
        m.dispose();
      });
    });
  }
  // 已载房间缓存:key → { group(已调好材质/居中), R, longProjPos, wallZ }。
  //   02 来回切换最常见(暗↔夜对比),不缓存的话每次都重下+重解析 GLB(主线程卡顿数百 ms);
  //   缓存命中=纯加回场景,零等待。最多存 2 间(当前+上一间),再多按 LRU 淘汰销毁——4 间全存显存吃不消(手机)。
  const roomCache = new Map();
  const ROOM_CACHE_MAX = 2;
  async function loadRoomModel(url) {
    const key = currentRoomKey;
    const hit = roomCache.get(key);
    if (hit) {                                   // 缓存命中:秒切(不重下、不重解析、材质早已调好)
      if (roomRef && roomRef !== hit.group) scene.remove(roomRef);   // 旧房间只摘下场景(仍在缓存里,别销毁)
      scene.add(hit.group);
      roomRef = hit.group; R = hit.R; longProjPos = hit.longProjPos; wallZ = hit.wallZ;
      roomCache.delete(key); roomCache.set(key, hit);                // LRU touch:挪到最新
      if (typeof window !== 'undefined' && window.__theater) window.__theater.R = R;
      return true;
    }
    const conf = ROOMS[key];      // 配置快照:await 下载期间 currentRoomKey 可能被更晚的切换改掉,不能再用 live cfg()
    const myToken = roomToken;    // 进入时的令牌:下载回来后若已变 → 本次已被取代,只入缓存、不上场景
    const roomG = await loadModel(url, 'room-' + key);
    if (!roomG) return false;
    // 先在「场景外」完成旋转/居中/量房/调材质,最后一步才换上场景——过期的加载不会顶掉用户后点的房间。
    const room = roomG.scene;
    room.rotation.y = (conf.rotY != null) ? conf.rotY : Math.PI;   // 旋转把该间“屏幕墙”转到 -X（三间都是 π;真旋转非镜像）
    room.updateMatrixWorld(true);
    let newR;
    const DIMS = conf.dims;
    if (DIMS) {
      // 显式量房（自动量房失效的开放式房间，如 night：外面大地形/弧形夜景板把整模型包围盒撑成 18×11×17）。
      //   按“室内地板”原始范围(rotY 前)算出旋转后的世界地板中心 → 居中 + 落地；R 直接取显式尺寸。
      //   W/D 已按 rotY 自适应换轴(下面 cR/sR),π 与 ±π/2 的房间都能直接量。
      const fc = new THREE.Vector3((DIMS.rawMinX + DIMS.rawMaxX) / 2, DIMS.rawFloorY, (DIMS.rawMinZ + DIMS.rawMaxZ) / 2);
      fc.applyMatrix4(room.matrixWorld);                  // position 仍 0 → 这是“仅旋转后”的世界地板中心
      room.position.set(-fc.x, -fc.y, -fc.z);             // 地板中心移到原点、地板落到 y=0
      room.updateMatrixWorld(true);
      const sx = DIMS.rawMaxX - DIMS.rawMinX, sz = DIMS.rawMaxZ - DIMS.rawMinZ;
      const cR = Math.abs(Math.cos(room.rotation.y)), sR = Math.abs(Math.sin(room.rotation.y));
      const W = sx * cR + sz * sR, D = sx * sR + sz * cR;   // 原始 X/Z 跨度按 rotY 投到世界轴(π:不变;±π/2:互换)→ 任意 90° 倍数旋转都量对
      newR = { W, D, H: DIMS.H, minX: -W / 2, maxX: W / 2, minZ: -D / 2, maxZ: D / 2 };
    } else {
      // 自动量房（KT3/dark 等闭合影院）：只按"房间外壳/地板脚印"量 R，剔除超大背景薄片。
      let shellBox = null, shellMin = -1;
      let floorBox = null, floorArea = -1;
      room.traverse((o) => {
        if (!o.isMesh) return;
        const b = new THREE.Box3().setFromObject(o);
        if (b.isEmpty()) return;
        const s = b.getSize(new THREE.Vector3());
        const mn = Math.min(s.x, s.y, s.z);   // 薄片(背景板/地板)最小边≈0；单块外壳房间最小边=层高，最大
        if (mn > shellMin) { shellMin = mn; shellBox = b; }
        // 地板脚印：接近水平的薄面(s.y<0.6)里、贴地(中心 y<1)、X*Z 面积最大者 = 地板。
        const c0 = b.getCenter(new THREE.Vector3());
        const area = s.x * s.z;
        if (s.y < 0.6 && c0.y < 1.0 && area > floorArea) { floorArea = area; floorBox = b; }
      });
      // 判据:地板脚印面积明显大于外壳脚印(>1.25×) → 改用地板脚印量长宽、按地板中心居中、层高取最高墙板;
      //   单块整体外壳的房间不触发、走外壳路径。dark:地板 ≫ 单块墙板 → 地板路径(6.99×6.17×3.19)。
      const shellSize = shellBox ? shellBox.getSize(new THREE.Vector3()) : null;
      const shellFA = shellSize ? shellSize.x * shellSize.z : 0;
      const useFloor = floorBox && floorArea > 4 && (shellFA < 0.5 || floorArea > shellFA * 1.25);
      let baseSize, cenX, cenZ, baseMinY, roomH;
      if (useFloor) {
        baseSize = floorBox.getSize(new THREE.Vector3());
        const fc = floorBox.getCenter(new THREE.Vector3());
        cenX = fc.x; cenZ = fc.z; baseMinY = floorBox.min.y;
        roomH = (shellBox && shellMin > 0.5) ? shellSize.y : baseSize.y;   // 层高取墙板高度(地板本身薄)
      } else {
        const baseBox = (shellBox && shellMin > 0.5) ? shellBox : new THREE.Box3().setFromObject(room);
        baseSize = baseBox.getSize(new THREE.Vector3());
        const c = baseBox.getCenter(new THREE.Vector3());
        cenX = c.x; cenZ = c.z; baseMinY = baseBox.min.y; roomH = baseSize.y;
      }
      room.position.x -= cenX; room.position.z -= cenZ; room.position.y -= baseMinY;   // 居中到原点、底部落到 y=0
      room.updateMatrixWorld(true);
      newR = { W: baseSize.x, D: baseSize.z, H: roomH, minX: -baseSize.x / 2, maxX: baseSize.x / 2, minZ: -baseSize.z / 2, maxZ: baseSize.z / 2 };
    }
    // 房间材质统一处理:双面渲染(内壁看得到)+ 各向异性 + 自发光照片背板 + 三个修正特例(见各分支)。
    // ⚠ 别按"颜色近黑"去改材质:深色窗框/格栅/黑框玻璃门是设计原貌,历史上两次按颜色处理都误伤过窗框。
    const selfLitNames = conf.selfLit || [];      // 自发光照片背板【按网格名】(如 night 室外夜景板)
    const selfLitMats = conf.selfLitMat || [];    // 自发光照片背板【按材质名】(网格名被多件共用的房间用,如新 day 海景板与地板/天花同名→只能按材质)
    const selfLitEmi = (conf.selfLitEmi != null ? conf.selfLitEmi : 1.0);   // 外景板亮度系数(per-room):板 toneMapped=false 不吃房间曝光,夜间房显得过亮 → 单独压暗(night 0.6),默认 1.0 其他房不变
    room.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach(fixTex);   // 各向异性 + minFilter mipmap 修正(防摩尔纹)
      const litByName = selfLitNames.indexOf(o.name) > -1;
      mats.forEach((m) => {
        if (!(litByName || (m.name && selfLitMats.indexOf(m.name) > -1))) return;
        // 照片背板点亮:emissive 拉白(乘回 emissiveMap 原图)+ 关色调映射 → 像屏幕画面一样按原图亮度常亮、不吃房间灯光
        if (m.emissive) m.emissive.setRGB(1, 1, 1);
        if (!m.emissiveMap && m.map) m.emissiveMap = m.map;   // 兜底:没有 emissiveMap 的板直接用基础贴图当自发光
        m.emissiveIntensity = selfLitEmi;   // per-room 外景板调光(night 压暗到 0.6,见 conf.selfLitEmi)
        m.toneMapped = false;
        m.needsUpdate = true;
      });
      mats.forEach((m) => {
        if (m.name === 'fallback Material') {
          // GLB 引用的材质丢失时 loader 兜底的"全黑金属"→ 整件家具变黑剪影(bright 初版 106 个网格中招:餐桌椅/厨房架/绿植)。
          // 占位成哑光灰绿(绿植自然、家具像莫兰迪绿),等模型师补好导出此分支自然不再命中。
          m.color.setHex(0x4a5d3a); m.metalness = 0; m.roughness = 0.9; m.needsUpdate = true;
        }
        if (m.roughness === 0 && m.metalness === 0 && !(m.transmission > 0)) m.roughness = 0.85;   // 粗糙度=0 的非玻璃非金属(导出缺省)会镜面反光发灰(bright 白墙/天花)→ 还原哑光
        if (m.transmission > 0) {                 // 窗玻璃：换成清透浅色玻璃（透射在本场景里发暗，改用半透明）
          m.transmission = 0;
          m.transparent = true;
          m.opacity = 0.22;
          if (m.color) m.color.set(0xdce8f1);     // 极浅冷白，像干净玻璃
          m.roughness = 0.06;
          m.metalness = 0;
          m.side = THREE.FrontSide;               // 透明面用单面，避免双面叠暗
          m.needsUpdate = true;
        } else {
          m.side = THREE.DoubleSide;
        }
      });
    });
    // 长焦·桌面投影仪“指定台面位”（如 night 放自带茶几上）：把 ROOMS[].longProj 的原始坐标经房间变换转成世界坐标，供 placeLongTable 用。
    let newLongProjPos = null;
    if (conf.longProj) {
      const lp = conf.longProj;
      newLongProjPos = room.localToWorld(new THREE.Vector3(lp.x, lp.y, lp.z));
    }
    // 屏幕特征墙中心(原始坐标点 {x,z})→ 世界 Z:屏幕/地柜/投影仪/相机整组锚到墙中心(没配=0=房间正中,dark 不变)。经房间真实变换换算,任意 rotY 都对。
    const newWallZ = conf.wallCRaw ? room.localToWorld(new THREE.Vector3(conf.wallCRaw.x, 0, conf.wallCRaw.z)).z : 0;
    // 入缓存;超上限时销毁「最久没用、且既非本次也非屏幕上那间」的(Map 按插入序遍历=LRU 序)
    roomCache.set(key, { group: room, R: newR, longProjPos: newLongProjPos, wallZ: newWallZ });
    for (const k of roomCache.keys()) {
      if (roomCache.size <= ROOM_CACHE_MAX) break;
      if (k === key || k === currentRoomKey) continue;
      const ev = roomCache.get(k); roomCache.delete(k); disposeTree(ev.group);
    }
    if (roomToken !== myToken) return false;   // 下载期间用户又切走了:本次只入缓存(下次点回秒切),不上场景
    // 真正换上场景(到这才动 roomRef/全局量)
    if (roomRef) scene.remove(roomRef);        // 旧房间摘下场景(仍在缓存里,淘汰时才销毁)
    scene.add(room);
    roomRef = room; R = newR; longProjPos = newLongProjPos; wallZ = newWallZ;
    if (typeof window !== 'undefined' && window.__theater) window.__theater.R = R;   // 换房间后调试钩子读到新 R
    return true;
  }
  await loadRoomModel(ROOM_URL);   // 首屏:载入起始房间(opts.roomUrl 覆盖 url;房间配置仍按 currentRoomKey 取)

  // -------------------------------------------------------------------------
  // 安装方式（正装 / 倒装）—— 先声明，下面 applyView() 会按 mountMode 决定取景
  // -------------------------------------------------------------------------
  //   投影仪 = 镜头(short短焦 / long长焦) × 安装(table桌面正装 / ceiling吸顶倒装) 的 2×2 矩阵。
  //   显示哪台 = 当前「镜头×安装」对应那台。各台按需加载：首屏只下短焦·桌面，其余切到才下(后台也会预热)。
  let throwType = 'short', mountMode = 'table';
  let cabInfo = null;   // 地柜柜面位置 {x,h}（地柜加载后写入），桌面投影仪据此坐落
  let cabinetRef = null;   // 地柜对象引用(初始化即按 cabWallGap 前移留墙边空隙,此后不动)
  const projRef     = { 'short-table': null, 'short-ceiling': null, 'long-table': null, 'long-ceiling': null };
  // short-table 预先标 true：它固定由首屏序列加载，提前占位防止 tb:ready 早调 setMount/setThrow 时
  // （此时首屏序列还没跑到第4步）又 ensureProjector 出第二台孤儿桌面投影仪 → 切吸顶时两台都显示。
  const projStarted = { 'short-table': true, 'short-ceiling': false, 'long-table': false, 'long-ceiling': false };
  const projURL     = {
    'short-table':   ASSETS.projector,
    'short-ceiling': ASSETS.projectorCeiling,
    'long-table':    ASSETS.projectorLong,
    'long-ceiling':  ASSETS.projectorCeilingLong
  };
  // 归一化后最长边(米)：短焦是超短焦机身大→0.5；长焦是迷你投影仪→收小，别显得太大；吸顶两台含吊杆,0.68(2026-06-12 用户嫌 0.56 小)
  const PROJ_SIZE   = { 'short-table': 0.5, 'short-ceiling': 0.68, 'long-table': 0.3, 'long-ceiling': 0.68 };
  function applyProjector() {                       // 只显示当前「镜头×安装」对应那台，其余隐藏
    const cur = throwType + '-' + mountMode;
    for (const k in projRef) { if (projRef[k]) projRef[k].visible = (k === cur); }
  }
  // 长焦·桌面：摆在沙发左侧(+Z 窗边)的小托盘上，镜头朝幕布 —— 长焦镜头本来就该离幕布远，
  // 摆幕布正下方的柜面反而不合理（那是短焦的位置）。偏移按沙发 GLB 托盘盘面实测：
  // 相对沙发原点 (-0.06, +1.06)，盘面顶 0.787m。沙发未就绪（用户抢在沙发加载完前点了长焦）
  // → 暂坐柜面，applyView()（沙发就位时必调）会再把它搬上托盘。
  const TRAY_OFF = { x: -0.06, topY: 0.787, z: 1.06 };
  function placeLongTable() {
    const p = projRef['long-table'];
    if (!p) return;
    if (longProjPos)  p.position.copy(longProjPos);   // 房间指定台面(如 night 放自带茶几上)，优先
    else if (sofaRef) p.position.set(sofaRef.position.x + TRAY_OFF.x, TRAY_OFF.topY, sofaRef.position.z + TRAY_OFF.z);
    else              p.position.set(cabInfo ? cabInfo.x : R.minX + 0.4, cabInfo ? cabInfo.h : 0.5, wallZ);
  }
  // 按需加载某组合的投影仪（URL 为空的组合跳过 → 以后扩展的占位机制）
  function ensureProjector(key) {
    if (projStarted[key] || projRef[key]) return Promise.resolve();
    const url = projURL[key];
    if (!url) return Promise.resolve();
    projStarted[key] = true;
    const ceiling = key.indexOf('-ceiling') > -1;
    return loadModel(url, 'proj-' + key).then((g) => {
      if (!g) { projStarted[key] = false; return; }
      const proj = wrapModel(g.scene, PROJ_SIZE[key] || 0.5, ceiling ? 'top-center' : 'bottom-center');
      proj.userData.kind = 'proj-' + key;
      let rotY = ceiling ? Math.PI : Math.PI / 2;          // 吸顶镜头朝幕布(-X)；短焦桌面(超短焦贴墙)朝 +X
      if (key === 'long-table') rotY -= Math.PI / 2;       // 长焦桌面 GLB 导出朝向比短焦差 90°；补转后(=0)镜头朝幕布(-X)，正好适配沙发旁托盘机位
      if (key === 'long-ceiling') rotY += Math.PI / 2;     // 新长焦吸顶 GLB(_1-opt)原生朝向偏 90°、露出侧面 → 左转 90° 让镜头正对幕布(-X)。若转反了把 += 改成 -=
      proj.rotation.y = rotY;
      if (ceiling)                       proj.position.set(R.minX + (key.indexOf('long') > -1 ? 2.3 : 1.1), R.H, wallZ);            // 吊在天花:短焦贴墙 1.1m;长焦投射远→后撤 2.3m
      else if (key !== 'long-table')     proj.position.set(cabInfo ? cabInfo.x : R.minX + 0.4, cabInfo ? cabInfo.h : 0.5, wallZ); // 短焦·桌面坐柜面
      scene.add(proj);
      projRef[key] = proj;
      if (key === 'long-table') placeLongTable();          // 长焦·桌面 → 沙发旁托盘
      applyProjector();
    });
  }
  // 切换安装方式 / 镜头类型：按需加载该组合 + 显隐 + 重新取景（吸顶 → 镜头自动仰看吊装机）
  const setMount = (m) => {
    if (m !== 'table' && m !== 'ceiling') return;
    mountMode = m; ensureProjector(throwType + '-' + m); applyProjector(); applyView();
  };
  const setThrow = (t) => {
    if (t !== 'short' && t !== 'long') return;
    throwType = t; ensureProjector(t + '-' + mountMode); applyProjector(); applyView();
  };

  // 当前步骤：吸顶的"仰看展示视角"只在「安装方式」步生效；切到别的步就归位看幕布。
  let activeStep = 'distance';
  const setActiveStep = (s) => { if (s) { activeStep = s; applyView(); } };

  // -------------------------------------------------------------------------
  // 固定视角（沿 X 轴正对投影仪/幕布墙）+ 观看距离联动
  // -------------------------------------------------------------------------
  //   相机始终 z=0 居中、朝 -X 正视幕布墙（不偏不斜 = "正面面对投影仪"），用户不能拖动。
  //   但右上角"观看距离"滑块会驱动 applyView()：把沙发(座位)摆到距幕布 d 米处，相机退到
  //   沙发后方一点跟拍 —— 于是拖滑块时画面会前后推拉（近 = 幕布占满，远 = 看到更多房间），
  //   配合幕布尺寸联动，直观演示"坐得越远、需要的幕布越大"。
  let sofaRef = null;
  const sofaPosTarget = new THREE.Vector3();   // 沙发目标位置：applyView 设它，渲染循环每帧 lerp 过去(和相机同速)→ 拖滑块不再一格一格硬跳
  let viewDist = 4.5;   // 当前观看距离(米)，默认与滑块初始值一致（4.5m）
  // 01「观看距离」联动：沙发随滑块在房间里前后移动（拖远=沙发后退、离幕布更远），相机始终跟在沙发后方固定一段。
  //   ·「相机-沙发」间距固定 → 沙发在画面里大小不变、侧托盘(放投影那个)始终留在画面内；变的是"沙发↔幕布"地面距离 +
  //     幕布在画面里的远近大小，直观演示"坐得越远、幕布看着越小、越要大屏"。（侧托盘在 z+1.06，窄竖屏水平半视野仅 25°
  //     → 间距须 ≥1.06/tan25°≈2.27m 托盘才不被切；缩间距让沙发"变大"在窄屏会同时丢竖直+水平，故改固定间距。）
  //   ·把滑块行程映射到"本房间放得下的纵深"(各房间深浅不同) → 整段滑块都看得到沙发在动，且远端永远留得下相机不出框。
  //     幕布尺寸推荐数值仍由 liquid 按真实米数算，这里只管 3D 示意。
  const VD_MIN = 2.7, VD_MAX = 6.8;   // 对应 liquid 距离滑块 min/max（改 liquid 滑块范围要同步这里；仅用于把行程映射到纵深）
  const SOFA_NEAR   = 1.2;          // 滑块最近端：沙发离幕布(米)
  const CAM_GAP     = 2.8;          // 相机跟拍间距(沙发后方·米)：最窄手机竖屏(aspect≈0.46,fov 撞 80°上限→水平半视野≈21°)下，侧托盘(z+1.06)要 ≥1.06/tan21°≈2.8m 才清楚留在画面内；整张沙发也完整可见
  const CAM_BACKOFF = 0.30;         // 相机离后墙留空(米)
  function applyView(snapView) {   // snapView=true 时沙发瞬移到位(初始/换房间)；否则只设目标，由渲染循环平滑 lerp(拖滑块用)
    const camBackLimit = R.maxX - CAM_BACKOFF;                       // 相机最远（贴后墙）
    const sofaFarX  = camBackLimit - CAM_GAP;                        // 滑块最远端：沙发 X（后方留得下 CAM_GAP）
    const sofaNearX = Math.min(R.minX + SOFA_NEAR, sofaFarX - 0.1);  // 最近端（极浅房间兜底：至少留一点行程）
    const t = Math.max(0, Math.min(1, (viewDist - VD_MIN) / (VD_MAX - VD_MIN)));
    const sofaX = sofaNearX + (sofaFarX - sofaNearX) * t;            // 沙发随滑块在[近,远]间线性移动
    sofaPosTarget.set(sofaX, 0, wallZ);                   // Z 同步当前房间的屏幕轴线(各房间 wallZ 不同,换房间时沙发要跟着挪正对屏幕)
    if (snapView && sofaRef) sofaRef.position.copy(sofaPosTarget);   // 初始/换房间：瞬移，不从旧位置飘过去
    if (mountMode === 'ceiling' && activeStep === 'mount') {
      // 吸顶展示视角：只在「安装方式」步生效。退到房间中部、站姿眼高，仰看吊在天花的投影仪 →
      // 不受观看距离影响，贴近幕布时也能完整看到倒装机。切到别的步 → 走下面的正常看幕布视角（归位）。
      camPosTarget.set(Math.min(R.minX + (throwType === 'long' ? 6.0 : 5.0), R.maxX - 0.2), 1.62, wallZ);   // 长焦:相机再后撤 1m（切到长焦→平滑后退,给出"投射更远"的视觉反馈;想更明显把 6.0 调大）
      camLookTarget.set(R.minX + 1.1, R.H * 0.66, wallZ);
    } else {
      // 正装：相机跟在沙发后方 CAM_GAP 米（不越后墙）→ 沙发大小恒定、托盘常驻画面；只有幕布远近随距离变。
      //   长焦·桌面机也摆在这个侧托盘上，CAM_GAP(2.5)≥2.27 → 「安装方式」步切到它时同样在画面内。
      const camX = Math.min(sofaX + CAM_GAP, camBackLimit);
      camPosTarget.set(camX, 1.4, wallZ);
      camLookTarget.set(R.minX + 0.25, R.H * 0.42, wallZ);
    }
    placeLongTable();   // 长焦·桌面跟随沙发（沙发刚就位 / 位置变化时同步搬上托盘）
  }
  applyView(true);                      // 房间就位后先按默认距离设好相机目标 + 沙发瞬移到位
  camera.position.copy(camPosTarget);   // 初始直接就位（不从占位位置慢慢飘过去）
  camLookNow.copy(camLookTarget);

  // 暴露给滑块：设置观看距离(米) → 移动沙发 + 相机（视角随距离前后推拉）
  const setViewingDistance = (d) => { if (d > 0) { viewDist = d; applyView(); } };

  // 01 按住距离滑杆 → 房间「蓝图虚化」：把房间壳(roomRef)所有网格临时换成同一块无光照的
  // 半透明幽灵材质 → 四个房间不管自身贴图/明暗,虚化后都是统一的淡蓝灰半透外壳;沙发/幕布/投影仪
  // 挂在 scene 顶层不受影响、保持实心 → 座位↔幕布这段距离凸显出来。松手换回原材质。
  // ⚠ 为什么换材质而不是调原材质的 opacity:带纹理 PBR 墙面半透后仍保留高光/环境反射,且日/亮房
  //   特征墙后面叠着玻璃墙/外景板,透出来像"墙变镜子";暗房黑墙透黑背景则完全看不出变化。
  const GHOST_OPACITY = 0.38;                 // 幽灵外壳的不透明度（想更透调小、更实调大）
  const GHOST_SHELL = 0x6f7a86;               // 烟灰磨砂壳色——四个房间统一用这一个（用户定版:不按房间底色换色）
  const ghostMat = new THREE.MeshBasicMaterial({   // 无光照+关色调映射 → 不受各房间灯光/曝光影响,观感稳定;背景保持各房原色不染
    color: GHOST_SHELL, transparent: true, opacity: GHOST_OPACITY, toneMapped: false,
    depthWrite: true,              // ⚠关键:写深度 → 最近的墙挡住后面的墙,不再前后墙层层半透叠加糊成近不透明色块(那样看着像刷了漆而非透明);DoubleSide 下最近面不论朝向都能挡,与各房间法线朝向无关
    side: THREE.DoubleSide         // 双面 → 法线朝外的壳体从屋内也可见,不同建模习惯的房间都不穿帮
  });
  let ghostOn = false;    // 目标状态（按住=true）
  let ghostRoom = null;   // 当前被换了材质的房间组：换房间时先把旧房间还原（roomCache 里不能躺着幽灵版）
  const setRoomGhost = (on) => { ghostOn = !!on; };
  function swapRoomMats(group, toGhost) {   // 整组换/还材质;原材质存在各网格 userData 上(随组缓存/销毁)
    group.traverse((o) => {
      if (!o.isMesh) return;
      if (toGhost) { if (o.userData.__mat === undefined) { o.userData.__mat = o.material; o.material = ghostMat; } }
      else if (o.userData.__mat !== undefined) { o.material = o.userData.__mat; o.userData.__mat = undefined; }
    });
  }
  function applyRoomGhost() {   // 渲染循环每帧调:即时同步虚化状态(常态两次比较就返回,零开销)
    // ⚠ 刻意不做透明度渐变:渐变的中间帧壳体必然接近隐形(opacity→0),背景整屏透出来——
    //   暗房闪黑、day/bright 闪白,进入/松开各闪一次(用户报过两回)。换材质是二值操作,
    //   即时切最干脆:任何一帧要么完整磨砂(0.38)、要么完整实景,不存在能闪的帧。
    if (ghostRoom && ghostRoom !== roomRef) { swapRoomMats(ghostRoom, false); ghostRoom = null; }   // 虚化中换了房间：旧房间复原
    if (!roomRef) return;
    if (ghostOn) {
      if (ghostRoom !== roomRef) { swapRoomMats(roomRef, true); ghostRoom = roomRef; }
    } else if (ghostRoom) {
      swapRoomMats(ghostRoom, false); ghostRoom = null;            // 松开:当帧整组还原,无渐隐
    }
  }

  // -------------------------------------------------------------------------
  // 幕布尺寸参数 —— 屏幕形态模型的定位/缩放基准
  // -------------------------------------------------------------------------
  let screenWidthM = 2.92;     // 画面宽(米)，随观看距离变（默认 4.5m → 132″ ≈ 2.92m）;保持"真实推荐值",屏幕中央的尺寸标签按它反算 → 标签与右上角面板始终同一个数
  // ⚠ 3D 幕布【物理缩放】—— 整体等比缩,不是封顶。
  //   滑杆最远端 200″ 的真实画面宽是 4.428m,但房间 GLB 的特征墙装不下(实测 bright 木纹墙
  //   2022-1747265_1 的 Z 跨仅 3.999m)→ 按真实米数摆会比墙还宽、两边各溢出 0.21m 穿帮。
  //   故 3D 幕布【全档】按 SCREEN_FIT 等比缩,让最远端正好落在 3.897m(贴墙极限,每边余 5cm,实测刚好合适):
  //     · 拖动全程连续变大(旧方案用 Math.min 封顶 → 6.0m 以后卡住不长,是错的)
  //     · 各档比例不失真(200″ 仍是 79″ 的 2.53 倍),只是整体体量 ×0.88、每格增幅相应变小
  //   数字完全不受影响:screenWidthM 仍是真实推荐值 → 面板与幕布中央标签照常 79″…132″…200″。
  //   ⚠ 改滑杆上限(liquid max)要同步 SCREEN_REC_MAX_W,否则最远端不再正好贴墙。
  const SCREEN_WALL_MAX_W = 3.897;   // 特征墙能装下的最大画面宽(米)
  const SCREEN_REC_MAX_W  = 4.428;   // 滑杆最远端 200″ 的真实画面宽(米) = 200 × 16/√337 × 0.0254
  const SCREEN_FIT = SCREEN_WALL_MAX_W / SCREEN_REC_MAX_W;   // ≈0.880
  let cabinetTopY = 0.30;      // 柜面高度（地柜加载完后更新）；屏幕底锚在它上方
  const SCREEN_BOTTOM_GAP = 0.22;   // 屏幕底离柜面的间距
  // 屏幕前移量 —— 按【当前房间】live 读 cfg()(换房间后自动取新房间的值);降落屏可用 screenInsetDrop 单独配(体薄,贴墙更近):
  const insetFor = (kind) => (kind === 'screen-fixed') ? (cfg().screenInset || 0.05)
    : (kind === 'screen-drop' && cfg().screenInsetDrop != null) ? cfg().screenInsetDrop
    : (cfg().screenInsetLift || cfg().screenInset || 0.05);

  // 暴露给弹窗滑块：设置画面宽度(米) → 缩放三种屏幕形态模型
  const setScreenWidth = (meters) => {
    if (meters > 0) { screenWidthM = meters; fitForms(); }
  };

  // -------------------------------------------------------------------------
  // 屏幕形态（画框固定 / 降落 / 地升）—— 由「屏幕形态」步骤驱动
  // -------------------------------------------------------------------------
  //   fixed=画框固定式(默认) / drop=降落屏 / floor=地升屏。模型随画面宽缩放、贴墙、按各自锚点定位。
  let screenFixedRef = null, screenDropRef = null, screenFloorRef = null, screenForm = 'fixed';
  // 各形态整体微缩系数：按幕布对齐后，降落/地升带外壳/底座 → 整体比固定画框显大，各自×系数收一点。1=不缩，可调。
  const FORM_TRIM = { 'screen-fixed': 1.0, 'screen-drop': 0.9, 'screen-floor': 0.9 };
  function placeFormAt(ref, scl) {   // 按给定缩放值摆放：缩放 + 按形态各自的竖向锚点定位（每帧 lerp 时也走这里,锚点全程不飘）
    ref.scale.setScalar(scl);
    const halfH = (ref.userData.size.y * scl) / 2;   // 该缩放下模型实际半高（含外壳/底座）
    // 三种形态各自的竖向锚点：地升屏坐地、降落屏贴顶、画框固定挂在柜面上方
    let py;
    if (ref.userData.kind === 'screen-floor') {
      py = halfH + 0.005;                               // 地升屏：底座落在地面 (y=0)
    } else if (ref.userData.kind === 'screen-drop') {
      py = ((cfg().dropCeilY || R.H) - 0.02) - halfH;   // 降落屏：外壳贴吊装面(局部下吊顶/星空板的底面,没配=层高 R.H)
    } else {
      py = cabinetTopY + SCREEN_BOTTOM_GAP + halfH;     // 画框固定：底在柜面上方
    }
    // 升降屏(降落/地升)与固定屏各自的前移量(按当前房间 cfg() 读);Z 锚到特征墙中心(wallZ,默认 0)。
    ref.position.set(R.minX + insetFor(ref.userData.kind), py, wallZ);   // 贴 -X 墙(墙厚→前移避免被墙吞)
  }
  function fitFormModel(ref, snap) {
    if (!ref || !R) return;
    // 按"幕布本体"缩放（不是整体最长边）→ 三种屏画面基本同宽；再乘各形态微缩系数收一下整体大小。
    // 各模型外壳/底座占比不同，若按最长边缩放，幕布就会一大一小（地升屏底座最大 → 画面最小）。panelWidth 由 recenterScreenPanel 量得。
    // 缩放用 推荐宽 × SCREEN_FIT:全档等比缩 → 全程连续变大,最远端正好贴墙不溢出(见上方 SCREEN_FIT)。
    const wForScale = screenWidthM * SCREEN_FIT;
    ref.userData.sclTarget = wForScale / (ref.userData.panelWidth || 1) * (FORM_TRIM[ref.userData.kind] || 1);
    // snap=初始摆放/换房间：瞬间到位。拖滑杆(setScreenWidth)不传 → 只设目标,渲染循环每帧 lerp 过去,
    // 幕布缩放与沙发/相机同速平滑（否则沙发在滑、幕布一格格跳,不同步）。
    if (snap) placeFormAt(ref, ref.userData.sclTarget);
  }
  function fitForms(snap) { fitFormModel(screenFixedRef, snap); fitFormModel(screenDropRef, snap); fitFormModel(screenFloorRef, snap); }   // 标签由渲染循环每帧更新，这里不用单独调
  function lerpForms() {   // 渲染循环每帧调：幕布缩放向目标推进(与沙发同 0.12 速率);隐藏形态不必演,直接就位
    const refs = [screenFixedRef, screenDropRef, screenFloorRef];
    for (let i = 0; i < refs.length; i++) {
      const ref = refs[i];
      if (!ref || ref.userData.sclTarget == null) continue;
      const t = ref.userData.sclTarget, s = ref.scale.x;
      if (s === t) continue;
      placeFormAt(ref, (!ref.visible || Math.abs(t - s) < 0.001) ? t : s + (t - s) * 0.12);
    }
  }

  // —— 幕布中央「推荐尺寸」标签 —— 在画面正中显示 16:9 对角线英寸数，随观看距离(画面缩放)/屏幕形态实时更新。
  //   in-world Sprite(始终朝相机)+ 画布纹理；toneMapped=false 不随房间明暗变暗；半透明药丸底压在画面上保证看清。
  let sizeLabel = null, sizeLabelCanvas = null, sizeLabelTex = null, sizeLabelDiag = -1;
  function drawSizeLabel(diag) {
    const cv = sizeLabelCanvas, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.font = '800 154px system-ui, "Segoe UI", "Microsoft YaHei", sans-serif';
    const txt = diag + '"';
    const cx = cv.width / 2, cy = cv.height / 2 + 4;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // 无底框（用户要求去掉背景）：靠投影 + 细描边让白字压在任何画面上都看得清
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 2;
    ctx.lineWidth = 9; ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineJoin = 'round';
    ctx.strokeText(txt, cx, cy);
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(txt, cx, cy);
  }
  function ensureSizeLabel() {
    if (sizeLabel) return;
    sizeLabelCanvas = document.createElement('canvas');
    sizeLabelCanvas.width = 512; sizeLabelCanvas.height = 256;
    sizeLabelTex = new THREE.CanvasTexture(sizeLabelCanvas);
    sizeLabelTex.colorSpace = THREE.SRGBColorSpace; sizeLabelTex.anisotropy = maxAniso;
    const mat = new THREE.SpriteMaterial({ map: sizeLabelTex, transparent: true, depthWrite: false, toneMapped: false });
    sizeLabel = new THREE.Sprite(mat);
    sizeLabel.renderOrder = 50;
    scene.add(sizeLabel);
  }
  const _slBox = new THREE.Box3(), _slC = new THREE.Vector3(), _slS = new THREE.Vector3();
  function updateSizeLabel() {
    const ref = screenForm === 'drop' ? screenDropRef : screenForm === 'floor' ? screenFloorRef : screenFixedRef;
    const panel = ref && ref.userData.panelMesh;
    if (!ref || !ref.visible || !panel || screenAnim) { if (sizeLabel) sizeLabel.visible = false; return; }   // 卷帘动画(降落/地升)进行中先藏标签；动画结束 screenAnim 清空后再显示并定位到画面正中
    ensureSizeLabel();
    sizeLabel.visible = true;
    const diag = Math.round(screenWidthM / 0.0254 * Math.sqrt(337) / 16);   // 画面宽(米) → 16:9 对角线英寸
    if (diag !== sizeLabelDiag) { sizeLabelDiag = diag; drawSizeLabel(diag); sizeLabelTex.needsUpdate = true; }
    ref.updateMatrixWorld(true);                                            // 强刷世界矩阵：fitFormModel 刚改了 scale/position，否则 bbox 读到上一帧旧尺寸
    _slBox.setFromObject(panel); _slBox.getCenter(_slC); _slBox.getSize(_slS);
    sizeLabel.position.set(_slC.x + 0.05, _slC.y, _slC.z);                   // 画面正中、略探出屏面朝相机(+X)避免 z-fighting
    const hWorld = Math.max(_slS.y * 0.20, 0.20);                            // 药丸高约画面高的 0.20，随画面缩放
    sizeLabel.scale.set(hWorld * (sizeLabelCanvas.width / sizeLabelCanvas.height), hWorld, 1);
  }
  initReady = true;   // 至此渲染循环每帧要碰的 screenForm / sizeLabel / sofaRef / updateSizeLabel 等全部声明完毕（且已过 line 492 的 await loadRoomModel）→ 放行循环里的门控段
  function applyScreenForm() {                           // 只显示当前形态对应的实物屏幕模型
    if (screenFixedRef) screenFixedRef.visible = (screenForm === 'fixed');
    if (screenDropRef)  screenDropRef.visible  = (screenForm === 'drop');
    if (screenFloorRef) screenFloorRef.visible = (screenForm === 'floor');
    // 地柜固定不动:初始化即按 cabWallGap 前移留好墙边空隙,地升屏升在它后面那段空隙里。
  }
  const setScreenForm = (f) => {
    if (f !== 'fixed' && f !== 'drop' && f !== 'floor') return;
    if (f === 'drop')  loadScreen('screen-drop');      // 降落/地升屏按需加载（首次切到才下载）
    if (f === 'floor') loadScreen('screen-floor');
    const changed = (f !== screenForm);
    screenForm = f; applyScreenForm();
    if (changed) playScreenReveal(f);                  // 地升=升起 / 降落=降下 的卷帘动画（固定式不演示）
  };
  // 升降屏「抽拉」动画的部件拆分 —— 真实电动幕的机构(已查证:幕布卷在外壳/底座柜里,电机+升降架把整块幕布抽出来):
  //   外壳/底座(mount):卷轴盒(降落顶)/ 底座柜(地升底),全程实体、不动、不裁剪 = 幕布的"抽出口"。
  //   幕布组(sheet):画面+配重杆+侧索+升降臂,整组当一块"硬板"——从出口沿固定裁剪面平移抽出/缩回(画面跟着幕布走,正是"从柜里抽出来")。
  //   分类只两分(mount vs sheet),按世界 Y 占比(对缩放/平移/Y 旋转都不变,比按网格名稳)。
  function prepScreenParts(model, kind) {
    model.updateMatrixWorld(true);
    const whole = new THREE.Box3().setFromObject(model);
    const minY = whole.min.y, H = Math.max(whole.max.y - minY, 1e-4);
    const mountAtTop = (kind !== 'screen-floor');          // 降落=壳在顶;地升=底座在底
    // mount 只圈住"外壳/底座柜本体":地升底座柜真实结构 cyFrac≤0.065,屏幕下方的孔板吸音边框(网格_003)在 0.15
    //   → 阈值 0.10 落在两者空档,边框归 sheet 跟屏一起抽出(0.16 会把边框误划进底座、留在原地)。
    const mountFrac = 0.10;
    const mount = [], sheet = [];
    const tb = new THREE.Box3();
    model.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      tb.setFromObject(o);
      const cyFrac = ((tb.min.y + tb.max.y) / 2 - minY) / H;
      const isMount = mountAtTop ? (cyFrac > 1 - mountFrac) : (cyFrac < mountFrac);
      (isMount ? mount : sheet).push(o);
    });
    // 幕布组收进一个挂在 wrapper 下的子组,动画里整组平移(attach 保持世界位姿;wrapper 重定位/缩放时自动跟随)。
    // 裁剪面只挂在幕布组材质上:但幕布的配重杆/升降臂常与外壳柜共用 baked 材质(地升幕),直接挂会把外壳也裁掉
    // → 给幕布组的材质各克隆一份再挂裁剪面,外壳保留原材质不受影响。
    const sheetGroup = new THREE.Group();
    model.add(sheetGroup);
    model.updateMatrixWorld(true);
    sheet.forEach((o) => {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      const clones = mats.map((m) => { const c = m.clone(); c.clippingPlanes = [screenClip]; c.needsUpdate = true; return c; });
      o.material = Array.isArray(o.material) ? clones : clones[0];
      sheetGroup.attach(o);
    });
    model.userData.parts = { mount, sheet, sheetGroup };
  }
  // 点亮"画面"材质：带图片的屏把画面放在自发光贴图(emissiveMap)上，但模型 emissiveFactor=黑 → 画面被乘成全黑。
  //   这里把自发光拉满 + 关掉色调映射，让画面像真显示屏一样常亮、不随房间明暗变暗。无自发光贴图的屏(降落/地升)无影响。
  function lightScreenImage(model) {
    model.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        if (m && m.emissiveMap) {
          if (m.emissive) m.emissive.setRGB(1, 1, 1);
          if (m.color) m.color.setRGB(0, 0, 0);   // 画面纯靠自发光显示：去掉漫反射底色，房间灯光不再冲淡/抵消画面
          m.toneMapped = false;
          m.needsUpdate = true;
          screenImageMats.add(m);            // 纳入 screenEmi 联动（按这间房的画面亮度统一设）
        }
      });
    });
    applyScreenEmissive();                   // 立刻按当前房间 screenEmi 设好画面亮度
  }
  // 播放升降屏的「抽拉」动画：幕布组当一块硬板,从外壳/底座的固定出口平移抽出(画面跟着幕布走)。外壳/底座实体不动。
  //   裁剪面【固定】在出口(壳口/柜口),只让出口外那侧显示 → 幕布平移穿过它,缩在壳/柜里的那截被裁掉(被实体外壳遮住)。
  const REVEAL_DUR = 3600;   // 电动幕布升降时长(ms)：慢一点才有"电机徐徐抽幕"的细腻感(不是 UI 弹出)
  // 幕布组偏移:e=0 完全缩回(偏移=dir×travel),e=1 抽到位(0)。dir:地升幕缩回时沉入底座(下),降落幕缩回时进壳(上)。
  function applySheetOffset(a, e) {
    const sg = a.ref.userData.parts && a.ref.userData.parts.sheetGroup;
    if (sg) sg.position.y = (a.dir * a.travel * (1 - e)) / a.scaleY;   // ÷wrapper 缩放:世界位移转本地量
  }
  function playScreenReveal(form) {
    const ref = form === 'floor' ? screenFloorRef : form === 'drop' ? screenDropRef : null;
    screenAnim = null;
    const myToken = ++revealToken;                     // 标记本次播放；任何更晚的切换都会让本次的兜底失效
    if (!ref) { screenClip.constant = 1e6; return; }   // 固定式/模型未就绪：不裁剪，整屏直接显示
    const parts = ref.userData.parts;
    if (parts && parts.sheetGroup) parts.sheetGroup.position.y = 0;   // 先归位再量(量到的是自然展开位)
    ref.updateMatrixWorld(true);
    const sb = new THREE.Box3(); let hasS = false;
    (parts ? parts.sheet : []).forEach((o) => { sb.union(new THREE.Box3().setFromObject(o)); hasS = true; });
    if (!hasS) { screenClip.constant = 1e6; return; }  // 没有幕布网格:直接整屏显示
    const mb = new THREE.Box3(); let hasM = false;
    (parts ? parts.mount : []).forEach((o) => { mb.union(new THREE.Box3().setFromObject(o)); hasM = true; });
    const scaleY = ref.scale.y || 1;
    let slotY, travel, dir;
    if (form === 'floor') {
      slotY = hasM ? mb.max.y : sb.min.y;              // 底座柜顶 = 抽出口
      travel = sb.max.y - slotY;                       // 幕布要往上抽这么高
      dir = -1;                                        // 缩回=沉入底座(往下)
      screenClip.normal.set(0, 1, 0); screenClip.constant = -slotY;   // 固定:只留 y≥slotY(柜口以上),柜里那截裁掉
    } else {
      slotY = hasM ? mb.min.y : sb.max.y;              // 卷轴盒底 = 抽出口
      travel = slotY - sb.min.y;                       // 幕布要往下抽这么深
      dir = 1;                                         // 缩回=缩进壳(往上)
      screenClip.normal.set(0, -1, 0); screenClip.constant = slotY;   // 固定:只留 y≤slotY(壳口以下),壳里那截裁掉
    }
    screenAnim = { start: performance.now(), dur: REVEAL_DUR, ref, scaleY, travel, dir };
    applySheetOffset(screenAnim, 0);                   // 起始帧:完全缩回(只露出口边缘)
    // 兜底：rAF 被节流/动画被打断时 updateScreenAnim 可能跑不完,幕布会卡在缩回态(看着没屏/位置错)。
    //   setTimeout 不受 rAF 节流影响,到点强制抽到位 + 解除裁剪;若期间又切了形态(token 变了)就放弃。
    setTimeout(() => {
      if (revealToken !== myToken) return;
      screenClip.constant = 1e6;
      if (parts && parts.sheetGroup) parts.sheetGroup.position.y = 0;
      screenAnim = null;
    }, REVEAL_DUR + 300);
  }
  function updateScreenAnim() {
    const a = screenAnim;
    const t = Math.min(1, (performance.now() - a.start) / a.dur);
    // 梯形速度曲线：起步/停止各 12% 缓冲,中段匀速 —— 真实电动幕布是电机恒速升降,
    // 旧 easeInOutCubic 中段猛冲、两端拖沓,看着像 UI 弹出动画而不是抽幕布。
    const A = 0.12;
    let e;
    if (t < A)            e = t * t / (2 * A * (1 - A));                       // 起步缓冲（加速）
    else if (t > 1 - A)   e = 1 - (1 - t) * (1 - t) / (2 * A * (1 - A));       // 停止缓冲（减速）
    else                  e = (t - A / 2) / (1 - A);                           // 匀速主体
    applySheetOffset(a, e);                                                    // 幕布组平移抽出(裁剪面固定不动)
    if (t >= 1) {                                                              // 抽到位 → 解除裁剪 + 幕布归位,停止
      screenClip.constant = 1e6;
      const sg = a.ref.userData.parts && a.ref.userData.parts.sheetGroup;
      if (sg) sg.position.y = 0;
      screenAnim = null;
    }
  }

  // -------------------------------------------------------------------------
  // 屏幕形态模型：首屏只下载画框固定式（默认唯一可见的 hero）。降落/地升屏体积大，
  // 改「按需加载」—— 用户首次切到该形态才下载。避免初始就 8 个模型并发抢带宽
  // （实测该环境多文件并发会把每条流压到 ~18kB/s，串行单条却能跑满 ~320kB/s）。
  // -------------------------------------------------------------------------
  const screenStarted = { 'screen-fixed': false, 'screen-drop': false, 'screen-floor': false };
  function loadScreen(kind) {
    if (screenStarted[kind]) return Promise.resolve();
    screenStarted[kind] = true;
    const url = kind === 'screen-drop' ? ASSETS.screenDrop
              : kind === 'screen-floor' ? ASSETS.screenFloor
              : ASSETS.screenFixed;
    return loadModel(url, kind).then((g) => {
      if (!g) { screenStarted[kind] = false; return; }    // 失败 → 解锁，下次切到可重试
      const m = wrapModel(g.scene, 1, 'center');           // 归一化最长边=1m，再按画面宽缩放
      recenterScreenPanel(m);                              // 按幕面水平居中（修非对称构件导致的偏移）
      m.userData.kind = kind;
      m.rotation.y = Math.PI;                              // 幕面朝房间(+X)
      scene.add(m);
      if (kind === 'screen-drop')       { screenDropRef = m;  prepScreenParts(m, kind); }   // 升降屏拆部件(挂裁剪面 + 配重杆独立组)
      else if (kind === 'screen-floor') { screenFloorRef = m; prepScreenParts(m, kind); }
      else                              { screenFixedRef = m; }                          // 固定式不裁剪
      fitFormModel(m, true); applyScreenForm(); lightScreenImage(m);   // 刚到货的模型:瞬间就位(不从归一化尺寸慢慢长大)
      // 懒加载期间用户可能已停在该形态 → 模型一到补播一次升/降揭幕动画（固定式不演示）
      const f = kind === 'screen-drop' ? 'drop' : kind === 'screen-floor' ? 'floor' : 'fixed';
      if (f !== 'fixed' && screenForm === f) playScreenReveal(f);
    });
  }

  // （投影仪加载逻辑见上方 ensureProjector：短焦·桌面随首屏加载，其余按需。）

  // -------------------------------------------------------------------------
  // 家具“重新摆放”函数（换房间时复用）+ 运行时换房间 setRoom
  // -------------------------------------------------------------------------
  // 地柜:按当前 R 贴 -X 墙 + 写 cabInfo/cabinetTopY(屏幕/短焦投影仪据此定位)。
  function placeCabinet() {
    if (!cabinetRef) return;
    const cabDepth = cabinetRef.userData.size.x;            // 沿 X 进深(实测 0.417m)
    const gap = (cfg().cabWallGap != null) ? cfg().cabWallGap : 0.18;   // 地柜背板离 -X 墙的空隙(放地升屏);房间没配则默认近贴墙
    const cabX = R.minX + gap + cabDepth / 2;               // 背板落在 R.minX+gap、整体前移让出墙边空隙;此后固定不动(切屏不再移地柜)
    cabinetRef.position.set(cabX, 0, wallZ);                // Z 锚到特征墙中心(与屏幕同轴)
    cabinetTopY = cabinetRef.userData.size.y;               // 柜面高
    cabInfo = { x: cabX, h: cabinetTopY };
  }
  // 投影仪:把已加载的各台按当前 R / cabInfo / longProjPos 重新定位。
  function placeProjectors() {
    for (const k in projRef) {
      const p = projRef[k]; if (!p) continue;
      if (k.indexOf('-ceiling') > -1)  p.position.set(R.minX + (k.indexOf('long') > -1 ? 2.3 : 1.1), R.H, wallZ);   // 吸顶:短焦贴墙 1.1m;长焦后撤 2.3m
      else if (k === 'long-table')     placeLongTable();                           // 长焦桌面:指定台面/沙发托盘
      else                             p.position.set(cabInfo ? cabInfo.x : R.minX + 0.4, cabInfo ? cabInfo.h : 0.5, wallZ);   // 短焦桌面:柜面
    }
  }
  // 我们的沙发:懒加载(自带沙发的房间不加载)。换到“需要我们沙发”的房间时确保它在。
  async function ensureSofa() {
    if (sofaRef || sofaStarted) return;
    sofaStarted = true;
    const sg = await loadModel(ASSETS.sofa, 'sofa');
    if (!sg) { sofaStarted = false; return; }
    const sofa = wrapModel(sg.scene, 2.4, 'bottom-center');
    sofa.userData.kind = 'sofa';
    sofa.rotation.y = Math.PI / 2;            // 面朝幕布（-X 墙）
    sofa.position.set(R.maxX * 0.42, 0, wallZ);   // 初始落位;正式位置由 applyView 统一摆(x=距幕布 3.3m, z=wallZ 与屏幕同轴)
    scene.add(sofa);
    sofaRef = sofa;
    applyView(true);                          // 沙发就位 → 按观看距离瞬移摆好 + 相机跟拍
  }
  // 运行时换房间（02 照明按钮驱动）:旧房间摘下 → 新房间(缓存命中秒切/否则加载) → 量 R → 重摆家具 → 换光。
  //   同一个 GLB(day 现别名 dark)不重载,只切配置+光;token 防快速连点把旧切换盖到新的上。
  async function setRoom(key) {
    if (!ROOMS[key]) return;
    if (key === currentRoomKey) { applyRoomLight(cfg().mode); return; }
    const prevUrl = cfg().url;
    currentRoomKey = key;
    if (cfg().url === prevUrl) { applyRoomLight(cfg().mode); return; }   // 同 GLB(占位别名)→ 不重载,只切光
    const myToken = ++roomToken;
    // 注意:不在这里就换光——旧房间还显示时提前换光会很怪;等新房间真就位(下面)再连同家具/光一起切。
    //   缓存命中=立即;否则要下载(已被后台预取进 HTTP 缓存的话只剩解析)。liquid 按钮高亮已即时反馈。
    const ok = await loadRoomModel(cfg().url);
    if (!ok || myToken !== roomToken) return;         // 载入失败 / 被更晚的切换取代 → 放弃
    placeCabinet();                                   // 地柜按新 R 贴墙 + 更新 cabInfo
    if (cfg().hasSofa === false) { if (sofaRef) sofaRef.visible = false; }   // 自带沙发的房间:藏我们的
    else { await ensureSofa(); if (sofaRef) sofaRef.visible = true; }
    fitForms(true);                                   // 三种屏按新 R + 新 inset 重定位(换房间瞬移,同 applyView)
    placeProjectors();                                // 投影仪按新 R/cabInfo/longProjPos 重定位
    applyScreenForm();                                // 维持当前形态显隐
    applyView(true);                                  // 沙发(若有)+相机按新 R 取景(换房间瞬移到位)
    applyRoomLight(cfg().mode);                       // 再套一次:把新房间里新到位的屏幕画面 screenEmi 对齐
  }

  // -------------------------------------------------------------------------
  // 首屏加载：串行下载「首屏必需」的模型 —— 地柜 → 画框屏 → 沙发 → 桌面投影仪。
  // 串行而非并发：实测本环境并发会把带宽切碎(每条~18kB/s)，串行反而每个都跑满(~320kB/s)，
  // 首屏整体更快、重要部件先出现。降落/地升屏 + 吸顶投影仪不在首屏 → 懒加载。
  // -------------------------------------------------------------------------
  (async () => {
    // 1) 地柜 —— 贴左墙、幕布正下方、落地。屏幕/投影仪的竖向定位都依赖柜面高 → 先加载。
    const cg = await loadModel(ASSETS.cabinet, 'cabinet');
    if (cg) {
      // 3.29 = 该 GLB 原生最长边(真实比例 3.29×0.37×0.42m);wrapModel 按最长边归一化,传原生值=不缩放。
      const cabinet = wrapModel(cg.scene, 3.29, 'bottom-center');
      cabinet.userData.kind = 'cabinet';
      cabinet.rotation.y = Math.PI;            // 正面原生朝 -X(墙)→ 转 180° 面向房间
      scene.add(cabinet);
      cabinetRef = cabinet;
      placeCabinet();                          // 按 R 贴墙 + 写 cabInfo/cabinetTopY
      fitForms(true);                          // 柜面高就绪 → 屏幕竖向锚点瞬间校正(此时屏幕多半还没加载,是兜底)
    }
    // 2) 画框固定屏（默认唯一可见的 hero）
    await loadScreen('screen-fixed');
    // 3) 沙发 —— 自带沙发的房间(cfg().hasSofa===false,如 night)跳过(用自带的);否则懒加载我们的沙发。
    if (cfg().hasSofa !== false) await ensureSofa();
    // 4) 短焦·桌面投影仪（默认可见的 short-table）—— 坐在地柜柜面上
    projStarted['short-table'] = true;
    const pg = await loadModel(ASSETS.projector, 'proj-short-table');
    if (pg) {
      const projector = wrapModel(pg.scene, 0.5, 'bottom-center');
      projector.userData.kind = 'proj-short-table';
      projector.rotation.y = Math.PI / 2;
      projector.position.set(cabInfo ? cabInfo.x : R.minX + 0.4, cabInfo ? cabInfo.h : 0.5, wallZ);
      scene.add(projector);
      projRef['short-table'] = projector;
      applyProjector();                    // 按当前镜头×安装决定显隐（默认 short-table → 显示）
    } else { projStarted['short-table'] = false; }

    // 5) 后台预热：首屏已就位 → 趁空闲串行把其余懒加载模型悄悄下好，用户切到时秒显、不用现等。
    //    串行下载不抢首屏带宽、单条跑满；已加载的会自动跳过。先投影仪(第3步)后屏幕(第4步)，按用户路径顺序。
    await ensureProjector('long-table');      // 长焦·桌面（用户正在试的）
    await ensureProjector('long-ceiling');    // 长焦·吸顶
    await ensureProjector('short-ceiling');   // 短焦·吸顶
    await loadScreen('screen-drop');          // 降落屏
    await loadScreen('screen-floor');         // 地升屏
    applyRoomLight(CUR);                       // 全部懒加载模型就位后再套一次这间的固定柔光(把新到的屏幕画面亮度对齐 screenEmi)

    // 6) 房间预取：其余房间的 GLB 字节悄悄拉进浏览器 HTTP 缓存（只下载、不解析、不占显存）。
    //    点 02 换房间时免网络等待、只剩解析；再配合 roomCache,来回切换零等待。串行、低优先级、失败忽略（真切换时会再下）。
    const fetched = new Set([cfg().url]);
    for (const k of ['night', 'dark', 'bright', 'day']) {
      const u = ROOMS[k] && ROOMS[k].url;
      if (!u || fetched.has(u)) continue;
      fetched.add(u);
      try { await (await fetch(u, { priority: 'low' })).arrayBuffer(); } catch (e) { /* 预取失败不要紧 */ }
    }
  })();

  // 调试钩子（仅本地预览用，上线无副作用）
  if (typeof window !== 'undefined') {
    window.__theater = { scene, camera, renderer, R,
      dbg: () => ({ initReady, room: !!roomRef, ghostOn, swapped: !!ghostRoom }),
      dbgStep: (n) => { for (let i = 0; i < (n || 1); i++) {   // 手动步进动画(相机/沙发/幕布缩放/虚化):预览标签页后台 rAF 冻结时,验证脚本用它代替真实帧推进
        camera.position.lerp(camPosTarget, 0.12); camLookNow.lerp(camLookTarget, 0.12); camera.lookAt(camLookNow);
        if (sofaRef) sofaRef.position.lerp(sofaPosTarget, 0.12);
        lerpForms(); updateSizeLabel(); applyRoomGhost();   // 与 loop() 的 initReady 段保持同款,漏一项就验不到(如尺寸标签)
      } } };
  }

  // 控制接口回传给 liquid（弹窗滑块联动幕布大小 + 观看距离视角）
  return { setScreenWidth, setViewingDistance, setRoomGhost, setBrightness, setMount, setThrow, setScreenForm, setActiveStep };
}
